import React, { useEffect } from 'react';

import Flag from 'react-flags';
import toast from 'react-hot-toast';
import { Close } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import styles from './JobsFullView.module.css';
import BubbleButton from '../../../Buttons/BubbleButton/BubbleButton';
import { updateFavourites } from '../../../../../app/features/userSlice';
import { getCurrencySymbol } from '../../../../../utils/formatHelpers';
import { ExclusiveJobIcon, SaveBookMark, SaveIcon } from '../../../../../assets/images/icons';
import { addJobToFavourites, removeJobFromFavourites } from '../../../../../services/jobService';
import { useGetEducationLevelsHook, useGetJobCategoriesHook } from '../../../../../utils/utilityHooks';

function JobsFullView({ fullViewList, setFullViewList, ...props }) {
    const { t } = useTranslation();
    const job = fullViewList;
    const userToken = useSelector((state) => state.user.token);
    const user = useSelector((state) => state.user.user);
    const dispatch = useDispatch();

    useEffect(() => {
        const jobElement = document.getElementById('jobFullView');
        if (jobElement) {
            jobElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    }, [fullViewList]);

    let city = job.city;
    if (t('general.city.' + city) !== 'general.city.' + city) {
        city = t('general.city.' + city);
    }

    const handleAddToFavourites = () => {
        addJobToFavourites({
            job_id: job.id,
            token: userToken,
        })
            .then(() => {
                dispatch(updateFavourites([...user.favourites, parseInt(job.id, 10)]));
                toast.success(t('job.job_added_to_favorites'));
            })
            .catch((error) => {
                if (error?.response?.data?.error) {
                    toast.error(error.response.data.error);
                }
            });
    };

    const handleRemoveFromFavourites = () => {
        removeJobFromFavourites({
            job_id: job.id,
            token: userToken,
        })
            .then(() => {
                dispatch(updateFavourites(user.favourites.filter((item) => item !== parseInt(job.id, 10))));
                toast.success(t('job.job_removed_from_favorites'));
            })
            .catch((error) => {
                if (error?.response?.data?.error) {
                    toast.error(error.response.data.error);
                }
            });
    };

    const desc = job.description;
    const detailUI = (name, value) => (
        <div className={styles.row}>
            <p className={styles.detailTitle}>{name}</p>
            <p className={styles.detailTitleValue}>{value}</p>
        </div>
    );

    let salary = '';
    let salary_period = t('general.UI.month');
    if (job?.currency && job?.currency !== 'undefined') {
        const currency = getCurrencySymbol(job?.currency);
        if (job.salary_from !== job.salary_to) {
            salary = currency + job.salary_from + ' - ' + currency + job.salary_to;
        } else {
            salary = currency + job.salary_from;
        }
    } else {
        const currency = getCurrencySymbol('usd');
        salary = currency + job.salary_from + ' - ' + currency + job.salary_to;
    }
    const educationLevels = useGetEducationLevelsHook();
    const categoryOptions = useGetJobCategoriesHook();
     return (
        <div id="jobFullView" className={`${styles.job_card}`} {...props}>
            <div className={styles.jobHeader}>
                <div className={styles.job_card_head_info} style={{ justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div className={styles.job_card_head_info_main}>
                        <h5 className={styles.jobTitle}>
                            {job.title}
                            {job.exlusiveJob && <img src={ExclusiveJobIcon} alt="Exclusive Job" />}
                        </h5>
                        <div>
                            <p
                                onClick={() => { window.location.href = `${'employer'}s/${job?.employerDetail?.slug}`; }}
                                style={{ cursor: "pointer", }}
                                className={styles.jobDescription}>
                                {job.employerDetail.company_name}
                            </p>


                        </div>
                        <div>
                            <Flag name={job.country} format="svg" shiny={false} basePath="/vendor/flags" />
                            <p className={styles.jobDescription}>{t('general.country.' + job.country)}, {city}</p>
                        </div>
                        <div className={styles.applyButtonContainer}>
                            <BubbleButton
                                to={`/jobs/${job.slug}`}
                                dark
                                small
                                headerDarkButton>
                                {t('general.UI.Apply_now')}
                            </BubbleButton>
                            {user?.account_type === 'jobseeker' && (
                                user?.favourites?.includes(parseInt(job.id, 10)) ? (
                                    <div className={styles.bookMarkContainer}>
                                        <img
                                            onClick={handleRemoveFromFavourites}
                                            src={SaveBookMark}
                                            className={styles.bookMarkIcon}
                                            style={{ margin: 0 }}
                                            alt="Remove from favorites"
                                        />
                                    </div>
                                ) : (
                                    <img
                                        style={{ margin: 0 }}
                                        onClick={handleAddToFavourites}
                                        src={SaveIcon}
                                        className={styles.bookMarkIcon}
                                        alt="Add to favorites"
                                    />
                                )
                            )}
                        </div>
                    </div>
                    <Close
                        className={styles.crossIcon}
                        style={{ cursor: 'pointer' }}
                        onClick={() => setFullViewList(false)}
                    />
                </div>
            </div>
            <div className={styles.jobBody}>
                <h5 className={styles.jobTitle}>{t('forms.manage_job.subtitle_description')}</h5>
                {desc.trim().replace(/<[^>]*>/g, '').length > 0 && (
                    <div className={styles.job_card_footer}>
                        <div dangerouslySetInnerHTML={{ __html: desc }} className={styles.jobDescription}></div>
                    </div>
                )}
            </div>
            <div className={styles.jobDetail}>
                <h5 className={styles.jobTitle}>{t('forms.manage_job.job_details')}</h5>
                {detailUI(t("forms.manage_job.jobCategory"), categoryOptions.filter((e) => e?.value == job?.category)[0]?.label)}
                {detailUI(t('jobs.salary'), `${salary}/${salary_period}`)}
                {detailUI(t("forms.manage_job.job_type"), t('general.UI.' + job.job_type))}
                {detailUI(t('talents_list.experience'), job.minworkexp + "-" + job.maxworkexp + " " + (job.maxworkexp > 0 ? t('general.UI.years') : t('general.UI.year')))}
                {detailUI(t('job_seeker.education'), educationLevels.filter((e) => e.value == job?.education_level)[0]?.label)}
                {/* {detailUI('Experience', job.maxworkexp+"-"job.maxworkexp'4 years')} */}
                {/* {detailUI('Education', 'Bachelors')} */}
            </div>
        </div>
    );
}

export default JobsFullView;
